import React, { useState, useEffect } from 'react';
import { FiSearch, FiPrinter, FiFileText, FiEye, FiPlus, FiEdit2 } from 'react-icons/fi';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';
import { socket } from '../../socket/socket';
import { FaCar, FaHashtag, FaPalette, FaMapMarkerAlt, FaTrash } from 'react-icons/fa';
import { printKOTDirect, printKOTSimple, checkThermalPrinter } from '../../utils/thermalPrinter';
import AddOrderModal from '../../components/AddOrderModal';
import BillUpdateRequestModal from '../../components/BillUpdateRequestModal';


const OrdersManagement = () => {
  const { user } = useAuth()
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    status: '',
    orderType: '',
    search: ''
  });
  const [showBillModal, setShowBillModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [discount, setDiscount] = useState({ amount: 0, type: 'percentage' });
  const [payment, setPayment] = useState({ cashAmount: '', onlineAmount: '' });
  const [isMc, setIsMc] = useState(false);
  const [cashOnly, setCashOnly] = useState(false);
  const [cashReceived, setCashReceived] = useState('');
  const [complimentary, setComplimentary] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showCreateOrderModal, setShowCreateOrderModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [selectedOrderForBilling, setSelectedOrderForBilling] = useState(null);
  const [gstNumber, setGstNumber] = useState('');
  const [additionalItems, setAdditionalItems] = useState([]);
  const [showBillUpdateModal, setShowBillUpdateModal] = useState(false);
  const [selectedOrderForUpdate, setSelectedOrderForUpdate] = useState(null);

  // Listen for custom event to open Add Order modal (from global Ctrl+A shortcut)
  useEffect(() => {
    const handleOpenAddOrderModal = () => {
      setShowCreateOrderModal(true);
      
      
    };

    window.addEventListener('openAddOrderModal', handleOpenAddOrderModal);

    return () => {
      window.removeEventListener('openAddOrderModal', handleOpenAddOrderModal);
    };
  }, []);

  // Listen for bill update notifications
  useEffect(() => {
    if (socket) {
      socket.on('billUpdateApproved', (data) => {
        toast.success(`Bill update request approved for order ${data.orderNumber}!`, {
          duration: 5000,
          icon: '✅'
        });
        fetchOrders(); // Refresh orders to show updated bill
      });

      socket.on('billUpdateRejected', (data) => {
        toast.error(`Bill update request rejected for order ${data.orderNumber}: ${data.rejectionReason}`, {
          duration: 5000,
          icon: '❌'
        });
      });

      return () => {
        socket.off('billUpdateApproved');
        socket.off('billUpdateRejected');
      };
    }
  }, [socket]);
  const [itemSearchTerm, setItemSearchTerm] = useState('');
  const [selectedDateFilter, setSelectedDateFilter] = useState('today'); // 'today', 'yesterday', or null

  // Date filter functions
  const handleTodayFilter = () => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0]; // Format: 2026-03-20
    setSelectedDateFilter('today');
    fetchOrders({
      startDate: dateStr,
      endDate: dateStr
    });
  };

  const handleYesterdayFilter = () => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const dateStr = yesterday.toISOString().split('T')[0]; // Format: 2026-03-19
    setSelectedDateFilter('yesterday');
    fetchOrders({
      startDate: dateStr,
      endDate: dateStr
    });
  };

  const handleClearDateFilter = () => {
    // Clear function disabled - user cannot clear the filter
    console.log('Filter clearing is disabled');
  };

  useEffect(() => {
    // Fetch orders with current date filter
    if (selectedDateFilter === 'today') {
      handleTodayFilter();
    } else if (selectedDateFilter === 'yesterday') {
      handleYesterdayFilter();
    } else {
      fetchOrders(); // Fallback without date filter
    }
  }, [filters]);

  // Apply today filter on initial load
  useEffect(() => {
    handleTodayFilter();
  }, []);

  useEffect(() => {
    if (!user) return;

    socket.on('notification:new', (payload) => {
      // Maintain current date filter
      if (selectedDateFilter === 'today') {
        handleTodayFilter();
      } else if (selectedDateFilter === 'yesterday') {
        handleYesterdayFilter();
      }
    });
    socket.on('order:updated', (payload) => {
      // Maintain current date filter
      if (selectedDateFilter === 'today') {
        handleTodayFilter();
      } else if (selectedDateFilter === 'yesterday') {
        handleYesterdayFilter();
      }
    });

    return () => {
      socket.off('notification:new');
      socket.off('order:updated');
    };
  }, [user, selectedDateFilter]);


  const fetchOrders = async (dateFilter = null) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.orderType) params.append('orderType', filters.orderType);
      
      // Add date filter if provided, otherwise use current selected filter
      let effectiveDateFilter = dateFilter;
      if (!effectiveDateFilter && selectedDateFilter) {
        if (selectedDateFilter === 'today') {
          const today = new Date();
          const dateStr = today.toISOString().split('T')[0];
          effectiveDateFilter = { startDate: dateStr, endDate: dateStr };
        } else if (selectedDateFilter === 'yesterday') {
          const yesterday = new Date();
          yesterday.setDate(yesterday.getDate() - 1);
          const dateStr = yesterday.toISOString().split('T')[0];
          effectiveDateFilter = { startDate: dateStr, endDate: dateStr };
        }
      }
      
      if (effectiveDateFilter) {
        params.append('startDate', effectiveDateFilter.startDate);
        params.append('endDate', effectiveDateFilter.endDate);
      }

      const response = await api.get(`/vendor/orders?${params.toString()}`);
      let ordersData = response.data.data || [];

      if (filters.search) {
        ordersData = ordersData.filter(order =>
          order.orderNumber?.toLowerCase().includes(filters.search.toLowerCase()) ||
          order.customerId?.fullName?.toLowerCase().includes(filters.search.toLowerCase())
        );
      }

      setOrders(ordersData);
    } catch (error) {
      toast.error('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };


  const handleOpenCreateOrder = () => {
    setShowCreateOrderModal(true);
  };

  const handleConfirmOrders = async () => {

    try {
      // Call API to accept all pending orders
      const response = await api.post('/vendor/orders/accept-all-pending');
      
      if (response.data.success) {
        // Show success message
        toast.success(`orders accepted successfully!`, {
          icon: '✅',
          duration: 3000
        });
      } else {
        throw new Error(response.data.message || 'Failed to accept orders');
      }
    } catch (error) {
      console.error('Error accepting orders:', error);
      toast.error(error.response?.data?.message || 'Failed to accept orders', {
        icon: '❌',
        duration: 4000
      });
    } finally {
      // Always dismiss loading toast
    }
  };

  const handleAddItemToOrder = (menuItem) => {
    const existingItem = additionalItems.find(item => item.menuItemId === menuItem._id);
    if (existingItem) {
      setAdditionalItems(
        additionalItems.map(item =>
          item.menuItemId === menuItem._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        )
      );
    } else {
      setAdditionalItems([...additionalItems, {
        menuItemId: menuItem._id,
        name: menuItem.name,
        price: menuItem.discountPrice || menuItem.price,
        quantity: 1
      }]);
    }
  };

  const handleRemoveAdditionalItem = (menuItemId) => {
    setAdditionalItems(additionalItems.filter(item => item.menuItemId !== menuItemId));
  };

  const handleUpdateAdditionalQuantity = (menuItemId, quantity) => {
    if (quantity <= 0) {
      handleRemoveAdditionalItem(menuItemId);
      return;
    }
    setAdditionalItems(
      additionalItems.map(item =>
        item.menuItemId === menuItemId
          ? { ...item, quantity: parseInt(quantity) }
          : item
      )
    );
  };

  const calculateAdditionalTotal = () => {
    return additionalItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };


  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await api.put(`/vendor/orders/${orderId}/status`, { status: newStatus });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getNextStatus = (currentStatus) => {
    const statusFlow = ['pending', 'accepted', 'preparing', 'ready', 'completed'];
    const currentIndex = statusFlow.indexOf(currentStatus);
    if (currentIndex === -1 || currentStatus === 'cancelled') return currentStatus;
    return statusFlow[currentIndex + 1] || 'completed';
  };

  const handleStatusClick = (orderId, currentStatus) => {
    if (currentStatus === 'completed' || currentStatus === 'cancelled') {
      // toast.info('Order is already ' + currentStatus);
      return;
    }
    const nextStatus = getNextStatus(currentStatus);
    updateOrderStatus(orderId, nextStatus);
  };

  const openBillingModal = (order) => {
    setSelectedOrderForBilling(order);
    setPayment({
      cashAmount: order.paymentDetails?.cashAmount || '',
      onlineAmount: order.paymentDetails?.onlineAmount || ''
    });
    setShowBillingModal(true);
  };

  const handleUpdatePayment = async () => {
    try {
      const cashAmt = Number(payment.cashAmount) || 0;
      const onlineAmt = Number(payment.onlineAmount) || 0;
      const totalPaid = cashAmt + onlineAmt;
      const orderTotal = selectedOrderForBilling.pricing?.total || 0;

      if (totalPaid !== orderTotal) {
        toast.error(`Total payment (₹${totalPaid.toFixed(2)}) must equal order total (₹${orderTotal.toFixed(2)})`);
        return;
      }

      await api.put(`/vendor/orders/${selectedOrderForBilling._id}/payment`, {
        cashAmount: cashAmt,
        onlineAmount: onlineAmt
      });

      toast.success('Payment details updated successfully');
      setShowBillingModal(false);
      setSelectedOrderForBilling(null);
      setPayment({ cashAmount: '', onlineAmount: '' });
      fetchOrders();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update payment');
    }
  };

  const generateKOT = async (orderId, useThermalPrinter = false) => {
    try {
      const response = await api.post(`/vendor/orders/${orderId}/kot`);
      const kotData = response.data.data;
      
      if (useThermalPrinter) {
        console.log(useThermalPrinter,"useThermalPrinteruseThermalPrinteruseThermalPrinter")
        // Try advanced thermal printer first
        const success = await printKOTDirect(kotData);
        if (success) {
          toast.success('KOT sent to thermal printer');
          return;
        }
        
        // Fallback to simple thermal printer
        const simpleSuccess = await printKOTSimple(kotData);
        if (simpleSuccess) {
          toast.success('KOT printed with thermal format');
          return;
        }
        
        // If both fail, use normal print
        toast.error('Thermal printer not available, using normal print...');
      }

        console.log(useThermalPrinter,"useThermalPrinteruseTherma")

      
      // Use thermalPrint format if available (new backend format)
      if (kotData.thermalPrint) {
        const tp = kotData.thermalPrint;
        const printContent = [
          ...tp.header,
          '',
          ...tp.details,
          tp.divider,
          '',
          ...tp.items,
          '',
          ...tp.footer
        ].join('\n');

        // Create print window with thermal format
        const printWindow = window.open('', '_blank', 'width=400,height=600');
        printWindow.document.write(`
          <html>
            <head>
              <title>KOT - ${kotData.kotNumber}</title>
              <style>
                @media print {
                  @page { 
                    margin: 2mm; 
                    size: 58mm auto;
                  }
                  body { margin: 0; }
                }
                body { 
                  font-family: 'Courier New', monospace; 
                  width: 58mm;
                  max-width: 58mm;
                  padding: 3mm;
                  font-size: 11px;
                  line-height: 1.3;
                  background: white;
                  color: black;
                  white-space: pre-wrap;
                  word-wrap: break-word;
                }
              </style>
            </head>
            <body>${printContent}</body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
        toast.success('KOT generated');
        return;
      }

      // Fallback: Normal print window with thermal printer optimization
      const printWindow = window.open('', '_blank', 'width=400,height=600');
      printWindow.document.write(`
        <html>
          <head>
            <title>KOT - ${kotData.orderNumber}</title>
            <style>
              @media print {
                @page { 
                  margin: 2mm; 
                  size: 58mm auto; /* Standard thermal printer width */
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                body { 
                  margin: 0; 
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
                * { 
                  -webkit-print-color-adjust: exact;
                  print-color-adjust: exact;
                }
              }
              * { 
                margin: 0; 
                padding: 0; 
                box-sizing: border-box; 
              }
              body { 
                font-family: 'Courier New', monospace; 
                width: 58mm; /* Standard 58mm thermal printer */
                max-width: 58mm;
                padding: 2mm;
                font-size: 10px;
                line-height: 1.2;
                background: white;
                color: black;
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .header {
                text-align: center;
                border-bottom: 1px dashed #000;
                padding-bottom: 5px;
                margin-bottom: 8px;
              }
              .header h2 {
                font-size: 14px;
                font-weight: bold;
                margin-bottom: 2px;
                letter-spacing: 1px;
              }
              .header .restaurant {
                font-size: 9px;
                margin-bottom: 1px;
                font-weight: bold;
              }
              .order-info {
                margin: 8px 0;
                border-bottom: 1px dashed #000;
                padding-bottom: 5px;
              }
              .order-info div {
                display: flex;
                justify-content: space-between;
                margin: 2px 0;
                font-size: 9px;
              }
              .order-info .label { 
                font-weight: bold; 
              }
              .items-section {
                margin: 8px 0;
              }
              .items-header {
                display: flex;
                justify-content: space-between;
                border-bottom: 1px solid #000;
                padding: 3px 0;
                font-weight: bold;
                font-size: 9px;
              }
              .item-row {
                display: flex;
                justify-content: space-between;
                padding: 4px 0;
                border-bottom: 1px dashed #ccc;
                page-break-inside: avoid;
              }
              .item-name {
                flex: 1;
                font-weight: bold;
                font-size: 9px;
                word-wrap: break-word;
              }
              .item-qty {
                width: 30px;
                text-align: center;
                font-size: 11px;
                font-weight: bold;
              }
              .item-notes {
                font-size: 8px;
                color: #555;
                margin-top: 1px;
                padding-left: 2px;
                word-wrap: break-word;
              }
              .offer-section {
                margin: 6px 0;
                padding: 4px;
                background: #f8f9fa;
                border-left: 2px solid #28a745;
                border-radius: 2px;
                page-break-inside: avoid;
              }
              .offer-title {
                font-size: 8px;
                color: #28a745;
                font-weight: bold;
                margin-bottom: 2px;
                text-transform: uppercase;
                letter-spacing: 0.3px;
              }
              .free-items {
                margin-top: 3px;
                padding-top: 3px;
                border-top: 1px dashed #28a745;
              }
              .free-item-row {
                display: flex;
                justify-content: space-between;
                align-items: center;
                margin: 1px 0;
                font-size: 8px;
              }
              .free-item-name {
                color: #6c757d;
                font-style: italic;
              }
              .free-item-qty {
                color: #28a745;
                font-weight: bold;
              }
              .special-instructions {
                margin: 8px 0;
                padding: 4px;
                background: #f5f5f5;
                border: 1px solid #ddd;
                border-radius: 2px;
                page-break-inside: avoid;
              }
              .special-instructions strong {
                display: block;
                margin-bottom: 2px;
                font-size: 9px;
              }
              .special-instructions p {
                font-size: 8px;
                line-height: 1.2;
                word-wrap: break-word;
              }
              .footer {
                text-align: center;
                margin-top: 10px;
                padding-top: 8px;
                border-top: 1px dashed #000;
                font-size: 9px;
              }
              /* Hide images for thermal printer */
              .header img.logo {
                display: none;
              }
              /* Ensure no color backgrounds */
              * {
                background: transparent !important;
              }
            </style>
          </head>
          <body>
            <div class="header">
              <h2>*** KOT ***</h2>
              <div class="restaurant">${kotData.restaurantName}</div>
            </div>
            
            <div class="order-info">
              <div><span class="label">Order #:</span><span>${kotData.orderNumber}</span></div>
              <div><span class="label">Table:</span><span>${kotData.tableNumber}</span></div>
              <div><span class="label">Order Type:</span><span>${kotData.orderType ? kotData.orderType.replace('-', ' ').toUpperCase() : 'DINE IN'}</span></div>
              <div><span class="label">Customer:</span><span>${kotData.customerName}</span></div>
              <div><span class="label">Time:</span><span>${new Date(kotData.orderTime).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })}</span></div>
            </div>
            
            <div class="items-section">
              <div class="items-header">
                <span>ITEM</span>
                <span>QTY</span>
              </div>
              ${kotData.items.map(item => `
                <div class="item-row">
                  <div style="flex: 1;">
                    <div class="item-name">${item.name}</div>
                    ${item.customizations && item.customizations.length > 0 ? `<div class="item-notes">Note: ${Array.isArray(item.customizations) ? item.customizations.join(', ') : item.customizations}</div>` : ''}
                    ${(item.bogoApplied === true || item.bogoApplied === 'true') && item.bogoDetails ? `
                      <div class="offer-section">
                        <div class="offer-title">Offer Applied: ${item.bogoDetails.offerText || 'Buy 1 Get 1 Free'}</div>
                        ${item.freeItems && item.freeItems.length > 0 ? `
                          <div class="free-items">
                            ${item.freeItems.map(freeItem => `
                              <div class="free-item-row">
                                <span class="free-item-name">${freeItem.name}</span>
                                <span class="free-item-qty">${freeItem.quantity}x</span>
                              </div>
                            `).join('')}
                          </div>
                        ` : ''}
                      </div>
                    ` : ''}
                  </div>
                  <div class="item-qty">${item.quantity}x</div>
                </div>
              `).join('')}
            </div>
            
            ${kotData.specialInstructions ? `
              <div class="special-instructions">
                <strong>⚠ SPECIAL INSTRUCTIONS:</strong>
                <p>${kotData.specialInstructions}</p>
              </div>
            ` : ''}
            
            <div class="footer">
              <p>━━━━━━━━━━━━━━━━━━━━</p>
              <p style="margin-top: 3px;">${kotData.orderType ? kotData.orderType.replace('-', ' ').toUpperCase() : 'DINE IN'}</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
      toast.success('KOT generated');
    } catch (error) {
      toast.error('Failed to generate KOT');
    }
  };

  const openBillModal = (order) => {
    setSelectedOrder(order);
    // Only reset discount and payment if bill is not generated (for generating new bill)
    if (!order.billGenerated) {
      setDiscount({ amount: 0, type: 'percentage' });
      setPayment({ cashAmount: '', onlineAmount: order?.pricing?.total || 0 });
      setIsMc(order.isMc || false);
      setCashOnly(false);
      setCashReceived('');
      setComplimentary(order.complimentary || '');
    } else {
      // For existing bills, use current bill data
      setDiscount(order.discount || { amount: 0, type: 'percentage' });
      setPayment(order.payment || { cashAmount: '', onlineAmount: order?.pricing?.total || 0 });
      setIsMc(order.isMc || false);
      setCashOnly(false);
      setCashReceived('');
      setComplimentary(order.complimentary || '');
    }
    setShowBillModal(true);
  };

  const openDetailsModal = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const openBillUpdateModal = (order) => {
    setSelectedOrderForUpdate(order);
    setShowBillUpdateModal(true);
  };

  const handleBillUpdateRequestSubmitted = (request) => {
    toast.success('Bill update request submitted successfully!');
    setShowBillUpdateModal(false);
    setSelectedOrderForUpdate(null);
    // Optionally refresh orders or update local state
  };

  const generateBill = async (billGenerated) => {
    try {
      let api_url = `/vendor/orders/${selectedOrder._id}/bill`
      if (billGenerated) {
        api_url = `/vendor/orders/${selectedOrder._id}/print-bill`
      }

      const response = await api.post(api_url, {
        discount: discount.amount,
        discountType: discount.type,
        cashAmount: payment.cashAmount || 0,
        onlineAmount: payment.onlineAmount || 0,
        gstNumber: gstNumber,
        isMc: isMc,
        complimentary: complimentary
      });
      const billData = response.data.data;

      // Open print window
      const printWindow = window.open('', '_blank');
      printWindow.document.write(`
        <html>
          <head>
            <title>Bill - ${billData.billNumber}</title>
            <style>
              @media print {
                @page { margin: 0; size: 80mm auto; }
                body { margin: 0; }
              }
              * { margin: 0; padding: 0; box-sizing: border-box; }
              body { 
                font-family: 'Courier New', monospace; 
                width: 80mm;
                padding: 5mm;
                font-size: 11px;
                line-height: 1.4;
              }
              .header {
                text-align: center;
                margin-bottom: 10px;
              }
              .header img.logo {
                width: 60px;
                height: 60px;
                object-fit: contain;
                margin-bottom: 8px;
                border-radius: 8px;
              }
              .header h1 {
                font-size: 20px;
                font-weight: bold;
                margin-bottom: 4px;
                letter-spacing: 1px;
              }
              .header .address {
                font-size: 10px;
                margin: 2px 0;
                line-height: 1.3;
              }
              .header .tax-info {
                font-size: 9px;
                margin-top: 4px;
                color: #555;
              }
              .qr-section {
                text-align: center;
                padding: 10px 0;
                border-top: 1px dashed #000;
                border-bottom: 1px dashed #000;
                margin: 10px 0;
              }
              .qr-section img {
                width: 120px;
                height: 120px;
                margin: 5px 0;
              }
              .qr-section p {
                font-size: 10px;
                font-weight: bold;
                margin-top: 4px;
              }
              .bill-info {
                margin: 10px 0;
                border-bottom: 1px dashed #000;
                padding-bottom: 8px;
              }
              .bill-info div {
                display: flex;
                justify-content: space-between;
                margin: 3px 0;
                font-size: 10px;
              }
              .bill-info .label { font-weight: bold; }
              .items-header {
                display: grid;
                grid-template-columns: 2fr 1fr 1fr 1fr;
                gap: 4px;
                border-bottom: 2px solid #000;
                padding: 5px 0;
                font-weight: bold;
                font-size: 10px;
              }
              .item-row {
                display: grid;
                grid-template-columns: 2fr 1fr 1fr 1fr;
                gap: 4px;
                padding: 6px 0;
                border-bottom: 1px dashed #ccc;
                font-size: 10px;
              }
              .item-row .name { font-weight: bold; }
              .item-row .qty, .item-row .price, .item-row .total {
                text-align: right;
              }
              .totals {
                margin-top: 10px;
                border-top: 2px solid #000;
                padding-top: 8px;
              }
              .totals div {
                display: flex;
                justify-content: space-between;
                margin: 4px 0;
                font-size: 11px;
              }
              .totals .grand-total {
                font-size: 14px;
                font-weight: bold;
                border-top: 2px solid #000;
                padding-top: 8px;
                margin-top: 8px;
              }
              .payment-info {
                text-align: center;
                margin: 10px 0;
                padding: 8px;
                background: #f5f5f5;
                border: 1px solid #ddd;
                border-radius: 4px;
                font-size: 11px;
              }
              .footer {
                text-align: center;
                margin-top: 15px;
                padding-top: 10px;
                border-top: 2px dashed #000;
                font-size: 10px;
              }
              .footer p { margin: 3px 0; }
            </style>
          </head>
          <body>
            <div class="header">
              ${user?.restaurantLogo ? `<img src="${user?.restaurantLogo}" alt="Logo" class="logo">` : ''}
              <h1>${billData.restaurantName}</h1>
              <div class="address">${billData.restaurantAddress}</div>
              <div class="tax-info">
                ${billData.restaurantGST ? `GST: ${billData.restaurantGST}` : ''}
                ${billData.restaurantGST && billData.restaurantFSSAI ? ' | ' : ''}
                ${billData.restaurantFSSAI ? `FSSAI: ${billData.restaurantFSSAI}` : ''}
              </div>
            </div>
            
            
            
            <div class="bill-info">
              <div><span class="label">Bill #:</span><span>${billData.billNumber}</span></div>
              <div><span class="label">Table:</span><span>${billData.tableNumber}</span></div>
              <div><span class="label">Customer:</span><span>${billData.customerName}</span></div>
              ${billData.isMc ? '<div><span class="label" style="color: #dc2626;">MC:</span><span style="color: #dc2626; font-weight: bold;">MANAGEMENT COMPLIMENTARY</span></div>' : ''}
              ${billData.complimentary ? `<div><span class="label" style="color: #059669;">🎁 Complimentary:</span><span style="color: #059669;">${billData.complimentary}</span></div>` : ''}
              ${billData.gstNumber ? `<div><span class="label">GST Number:</span><span>${billData.gstNumber}</span></div>` : ''}
              ${billData.customerMobile ? `<div><span class="label">Mobile:</span><span>${billData.customerMobile}</span></div>` : ''}
              <div><span class="label">Date:</span><span>${new Date(billData.orderTime).toLocaleString('en-IN', { dateStyle: 'short', timeStyle: 'short' })}</span></div>
            </div>
            
            <div class="items-header">
              <span>ITEM</span>
              <span style="text-align: right;">QTY</span>
              <span style="text-align: right;">PRICE</span>
              <span style="text-align: right;">TOTAL</span>
            </div>
            
            ${billData.items.map(item => `
              <div class="item-row" style="${item.bogoApplied ? 'background: #d4edda; border-left: 3px solid #28a745; padding-left: 8px;' : ''}">
                <span class="name">
                  ${item.name}
                  ${item.bogoApplied && item.bogoDetails ? `<br/><small style="color: #28a745; font-size: 9px;">🎁 ${item.bogoDetails.offerText} (${item.bogoDetails.freeQuantity} free)</small>` : ''}
                </span>
                <span class="qty">${item.quantity}</span>
                <span class="price">₹${item.price}</span>
                <span class="total">₹${item.total}</span>
              </div>
            `).join('')}
            
            <div class="totals">
            <div>
              <span>Subtotal:</span>
              <span>₹${Number(billData.subtotal).toFixed(2)}</span>
            </div>

            ${Number(billData.couponDiscount) > 0 && !billData.isMc
          ? `
                <div style="color: #16a34a;">
                  <span>Coupon Discount${billData.couponDetails?.code ? ` (${billData.couponDetails.code})` : ''}:</span>
                  <span>- ₹${Number(billData.couponDiscount).toFixed(2)}</span>
                </div>
              `
          : ''
        }

            ${Number(billData.discount) > 0 && !billData.isMc
          ? `
                <div style="color: #16a34a;">
                  <span>Discount ${billData.discountType === 'percentage' ? '(%)' : ''}:</span>
                  <span>- ₹${Number(billData.discount).toFixed(2)}</span>
                </div>
              `
          : ''
        }

            ${billData.vendorDiscount?.applied && !billData.isMc
          ? `
                <div style="color: #16a34a;">
                  <span>Vendor Discount:</span>
                  <span>- ₹${Number(billData.vendorDiscount.discountAmount).toFixed(2)}</span>
                </div>
              `
          : ''
        }

            <div>
              <span>CGST:</span>
              <span>₹${Number(billData.cgstAmount).toFixed(2)}</span>
            </div>

            <div>
              <span>SGST:</span>
              <span>₹${Number(billData.sgstAmount).toFixed(2)}</span>
            </div>

            <div>
              <span>Delivery Charge:</span>
              <span>₹${Number(billData.deliveryCharge).toFixed(2)}</span>
            </div>

            <div class="grand-total">
              <span>${billData.isMc ? 'GRAND TOTAL (MC):' : 'GRAND TOTAL:'}</span>
              <span>₹${Number(billData.total).toFixed(2)}</span>
            </div>
          </div>
            ${billData.upiQRCode ? `
              <div class="qr-section">
                <img src="${billData.upiQRCode}" alt="UPI QR Code">
                <p>📱 SCAN TO PAY 📱</p>
              </div>
            ` : ''}
            
            <div class="footer">
              <p>━━━━━━━━━━━━━━━━━━━━</p>
              <p style="font-weight: bold; margin: 8px 0;">THANK YOU!</p>
              <p>Visit Again 😊</p>
            </div>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();

      setShowBillModal(false);
      setGstNumber('');
      toast.success('Bill generated');
      fetchOrders();
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to generate bill';
      alert(errorMessage);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'badge-warning',
      accepted: 'badge-info',
      preparing: 'badge-primary',
      ready: 'badge-success',
      completed: 'badge-success',
      cancelled: 'badge-danger'
    };
    return badges[status] || 'badge-info';
  };

  if (loading) {
    return <div className="loading"><div className="spinner"></div></div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-orange-50 ">
      {/* Page Header */}
      <div className="bg-white rounded-2xl p-3 sm:p-4 mb-3">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
          <div className="mb-3 md:mb-0">
            <h1 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 mb-1 sm:mb-2">Orders Management</h1>
            <p className="text-sm sm:text-base text-gray-600 hidden sm:block">View and manage all orders</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
            <button
              data-add-order-btn
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-1 sm:gap-2 group relative text-sm sm:text-base"
              onClick={handleOpenCreateOrder}
              title="Add Order (Alt+O)"
            >
              <FiPlus className="text-sm sm:text-base sm:text-lg" />
              <span className="hidden sm:inline">Add Order</span>
              <span className="sm:hidden">Add</span>
              <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
                Alt+O
              </span>
            </button>
            <button
              className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-3 sm:px-4 md:px-6 py-2 sm:py-3 rounded-xl font-semibold shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center gap-1 sm:gap-2 group relative text-sm sm:text-base"
              onClick={handleConfirmOrders}
              title="Accept Orders"
            >
              <FiFileText className="text-sm sm:text-base sm:text-lg" />
              <span className="hidden sm:inline">Accept Orders</span>
              <span className="sm:hidden">Accept</span>
            </button>
          </div>
        </div>
      </div>

      {/* Filters Section */}
      <div className="bg-white rounded-2xl p-4 mb-3">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-3">
          {/* Search Input */}
          <div className="relative lg:col-span-2">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 text-lg" />
            <input
              type="text"
              placeholder="Search by order number or customer..."
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="w-full pl-10 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200"
            />
          </div>

          {/* Status Filter */}
          <select
            value={filters.status}
            onChange={(e) => setFilters({ ...filters, status: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="preparing">Preparing</option>
            <option value="ready">Ready</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>

          {/* Order Type Filter */}
          <select
            value={filters.orderType}
            onChange={(e) => setFilters({ ...filters, orderType: e.target.value })}
            className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:border-blue-500 focus:outline-none transition-colors duration-200"
          >
            <option value="">All Types</option>
            <option value="dine-in">Dine-In</option>
            <option value="delivery">Delivery</option>
            <option value="pickup">Pickup</option>
          </select>

          {/* Today Button */}
          <button
            onClick={handleTodayFilter}
            className={`px-3 py-2 rounded-lg font-medium shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center justify-center gap-1 ${
              selectedDateFilter === 'today'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white ring-2 ring-blue-300 ring-offset-2'
                : 'bg-gray-500 hover:bg-gray-600 text-white'
            }`}
          >
            📅 Today
            {selectedDateFilter === 'today' && <span className="text-xs">✓</span>}
          </button>

          {/* Yesterday Button */}
          <button
            onClick={handleYesterdayFilter}
            className={`px-3 py-2 rounded-lg font-medium shadow-lg transform transition-all duration-200 hover:scale-105 flex items-center justify-center gap-1 ${
              selectedDateFilter === 'yesterday'
                ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white ring-2 ring-blue-300 ring-offset-2'
                : 'bg-gray-500 hover:bg-gray-600 text-white'
            }`}
          >
            📅 Yesterday
            {selectedDateFilter === 'yesterday' && <span className="text-xs">✓</span>}
          </button>
        </div>
      </div>

      {/* Orders Table */}
      <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Order #</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Customer</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Table</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Items</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Amount</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Type</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Status</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Time</th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-blue-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {orders.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((order) => (
                <tr key={order._id} className="hover:bg-gray-50 transition-colors duration-150">
                  <td className="px-6 py-4">
                    <span className="font-bold text-gray-900">{order.orderNumber}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{order.customerId?.fullName || 'Guest'}</div>
                      <div className="text-gray-500">{order.customerId?.mobileNumber || 'N/A'}</div>
                      {order.isMc && (
                        <div className="mt-1">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            MC
                          </span>
                        </div>
                      )}
                      {order.complimentary && (
                        <div className="mt-1 text-xs text-gray-600 max-w-[200px] truncate" title={order.complimentary}>
                          🎁 {order.complimentary}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">
                      {order.tableId?.tableNumber ?? 'N/A'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {order.items?.length || 0} items
                    </div>
                    <div className="text-xs text-gray-500 max-w-xs">
                      {order.items?.slice(0, 2).map((item, idx) => (
                        <div key={idx} className="flex justify-between items-center">
                          <span className="font-medium">{item.name}</span>
                          <span className="text-gray-400">x{item.quantity}</span>
                        </div>
                      ))}
                      {order.items?.length > 2 && (
                        <div className="text-gray-400">+{order.items.length - 2} more</div>
                      )}
                    </div>
                  </td>


                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1">
                      <span className="font-bold text-gray-900">
                        ₹{Number(order.pricing?.total || 0).toFixed(2)}
                      </span>
                      {(order.paymentDetails?.cashAmount > 0 || order.paymentDetails?.onlineAmount > 0) && (
                        <div className="text-xs text-gray-600">
                          {order.paymentDetails?.cashAmount > 0 && (
                            <div className="text-green-600">💵 Cash: ₹{order.paymentDetails.cashAmount.toFixed(2)}</div>
                          )}
                          {order.paymentDetails?.onlineAmount > 0 && (
                            <div className="text-blue-600">💳 Online: ₹{order.paymentDetails.onlineAmount.toFixed(2)}</div>
                          )}
                        </div>
                      )}
                    </div>
                  </td>

                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                      {order.orderType || 'dine-in'}
                    </span>

                    {order.orderType === 'pickup' && order.vehicleDetails && (
                      <div className="text-xs text-gray-600 mt-1 space-y-1">
                        <div><FaCar className="inline mr-1" />{order.vehicleDetails.vehicleType}</div>
                        <div><FaHashtag className="inline mr-1" />{order.vehicleDetails.vehicleNo}</div>
                        <div><FaPalette className="inline mr-1" />{order.vehicleDetails.color}</div>
                        <div><FaMapMarkerAlt className="inline mr-1" />{order.vehicleDetails.location}</div>
                      </div>
                    )}

                    {/* DELIVERY */}
                    {order.orderType === 'delivery' && order.deliveryAddress && (
                      <div className="text-xs text-gray-600 mt-1 space-y-1">
                        <div><FaMapMarkerAlt className="inline mr-1" />{order.deliveryAddress.address}</div>
                        <div>🏙 {order.deliveryAddress.city}, {order.deliveryAddress.state}</div>
                        <div>📮 {order.deliveryAddress.pincode}</div>
                      </div>
                    )}
                  </td>

                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium cursor-pointer transition-all duration-200 ${order.status === 'pending' ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200' :
                        order.status === 'accepted' ? 'bg-blue-100 text-blue-800 hover:bg-blue-200' :
                          order.status === 'preparing' ? 'bg-purple-100 text-purple-800 hover:bg-purple-200' :
                            order.status === 'ready' ? 'bg-green-100 text-green-800 hover:bg-green-200' :
                              order.status === 'completed' ? 'bg-gray-100 text-gray-600' :
                                order.status === 'cancelled' ? 'bg-red-100 text-red-600' :
                                  'bg-gray-100 text-gray-800'
                        } ${order.status === 'completed' || order.status === 'cancelled' ? 'cursor-not-allowed' : 'hover:scale-105'}`}
                      onClick={() => handleStatusClick(order._id, order.status)}
                      title={order.status === 'completed' || order.status === 'cancelled' ? 'Cannot change status' : `Click to move to ${getNextStatus(order.status)}`}
                    >
                      {order.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {new Date(order.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-2">
                      {/* {(order.status === 'pending' || order.status === 'accepted' || order.status === 'preparing') && order.createdBy === 'vendor' && (
                        <button
                          className="bg-green-500 hover:bg-green-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200"
                          onClick={() => handleOpenAddItems(order)}
                          title="Add More Items"
                        >
                          + Add Items
                        </button>
                      )} */}

                      <button
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200"
                        onClick={() => openDetailsModal(order)}
                        title="View Details"
                      >
                        <FiEye className="inline mr-1" />
                        View
                      </button>

                      <button
                        className="bg-gray-500 hover:bg-gray-600 text-white px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200"
                        onClick={() => generateKOT(order._id)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          // Show thermal printer option on right-click
                          if (window.confirm('Use Thermal Printer? Click OK for thermal, Cancel for normal printer')) {
                            generateKOT(order._id, true);
                          }
                        }}
                        title="Print KOT (Left-click: Normal, Right-click: Thermal)"
                      >
                        <FiPrinter className="inline mr-1" />
                        KOT
                      </button>
                      {
                        (
                          order.orderType !== 'dine-in' ||
                          (order.orderType === 'dine-in' && order.tableId?.tableNumber === 0)
                        ) && (
                          <div className="flex gap-2">
                            {!order.billGenerated ? (
                              <button
                                className="px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200 bg-orange-500 hover:bg-orange-600 text-white"
                                onClick={() => openBillModal(order)}
                                title="Generate Bill"
                              >
                                <FiFileText className="inline mr-1" />
                                Bill
                              </button>
                            ) : (
                              <>
                                <button
                                  className="px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200 bg-blue-500 hover:bg-blue-600 text-white"
                                  onClick={() => openBillModal(order)}
                                  title="View Bill"
                                >
                                  <FiFileText className="inline mr-1" />
                                  View Bill
                                </button>
                                <button
                                  className="px-3 py-1 rounded-lg text-xs font-medium transition-colors duration-200 bg-green-500 hover:bg-green-600 text-white"
                                  onClick={() => openBillUpdateModal(order)}
                                  title="Request Bill Update"
                                >
                                  <FiEdit2 className="inline mr-1" />
                                  Update
                                </button>
                              </>
                            )}
                          </div>
                        )
                      }
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {orders.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-400 text-lg mb-2">No orders found</div>
            <div className="text-gray-500 text-sm">Try adjusting your filters or create a new order</div>
          </div>
        )}

        {/* Pagination */}
        {Math.ceil(orders.length / itemsPerPage) > 1 && (
          <div className="bg-white rounded-lg shadow-sm mt-6 p-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
              <div className="text-sm text-gray-600">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, orders.length)}-{Math.min(currentPage * itemsPerPage, orders.length)} of {orders.length} orders
              </div>
              <div className="flex items-center space-x-2">
                <button
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                {[...Array(Math.ceil(orders.length / itemsPerPage))].map((_, idx) => {
                  const pageNum = idx + 1;
                  const totalOrderPages = Math.ceil(orders.length / itemsPerPage);
                  if (
                    pageNum === 1 ||
                    pageNum === totalOrderPages ||
                    (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                  ) {
                    return (
                      <button
                        key={pageNum}
                        className={`px-3 py-2 text-sm border rounded-lg transition-colors min-w-[36px] ${pageNum === currentPage
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'border-gray-300 hover:bg-gray-50'
                          }`}
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    );
                  } else if (pageNum === currentPage - 2 || pageNum === currentPage + 2) {
                    return <span key={pageNum} className="px-2 text-gray-400">...</span>;
                  }
                  return null;
                })}
                <button
                  className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  onClick={() => setCurrentPage(prev => Math.min(Math.ceil(orders.length / itemsPerPage), prev + 1))}
                  disabled={currentPage === Math.ceil(orders.length / itemsPerPage)}
                >
                  Next
                </button>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <span className="text-gray-600">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={(e) => {
                    setItemsPerPage(Number(e.target.value));
                    setCurrentPage(1);
                  }}
                  className="px-3 py-1 border border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none transition-colors"
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                </select>
                <span className="text-gray-600">per page</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Order Details Modal */}
      {showDetailsModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">Order Details - {selectedOrder.orderNumber}</h2>
              <button className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none transition-colors" onClick={() => setShowDetailsModal(false)}>×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">
              {/* Order Info */}
              <div className="mb-6">
                <h3 className="mb-4 text-blue-600 text-lg font-semibold">📋 Order Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-gray-50 p-5 rounded-xl border border-gray-200">
                  <div className="space-y-3">
                    <p className="text-sm">
                      <span className="text-gray-600 font-medium inline-block min-w-[120px]">Order Number:</span>
                      <span className="text-gray-900 font-medium">{selectedOrder.orderNumber}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-600 font-medium inline-block min-w-[120px]">Order Type:</span>
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-800 text-xs font-medium rounded-full">{selectedOrder.orderType}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-600 font-medium inline-block min-w-[120px]">Status:</span>
                      <span className={`inline-block px-3 py-1 text-xs font-medium rounded-full capitalize ${getStatusBadge(selectedOrder.status)}`}>{selectedOrder.status}</span>
                    </p>
                  </div>
                  <div className="space-y-3">
                    <p className="text-sm">
                      <span className="text-gray-600 font-medium inline-block min-w-[100px]">Table:</span>
                      <span className="text-gray-900 font-medium"> {selectedOrder.tableId?.tableNumber ?? 'N/A'}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-600 font-medium inline-block min-w-[100px]">Customer:</span>
                      <span className="text-gray-900 font-medium">{selectedOrder.customerId?.fullName || 'Guest'}</span>
                    </p>
                    <p className="text-sm">
                      <span className="text-gray-600 font-medium inline-block min-w-[100px]">Time:</span>
                      <span className="text-gray-900 font-medium">{new Date(selectedOrder.createdAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</span>
                    </p>
                  </div>
                </div>
                {selectedOrder.specialInstructions && (
                  <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                    <strong className="text-yellow-800 block mb-2">⚠️ Special Instructions:</strong>
                    <p className="text-yellow-700 text-sm leading-relaxed break-words">{selectedOrder.specialInstructions}</p>
                  </div>
                )}
              </div>

              {/* Order Items */}
              <div className="mb-6">
                <h3 className="mb-4 text-blue-600 text-lg font-semibold">🍽️ Order Items</h3>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="bg-blue-50">
                        <th className="px-4 py-3 text-left text-blue-800 font-semibold">Item</th>
                        <th className="px-4 py-3 text-center text-blue-800 font-semibold">Quantity</th>
                        <th className="px-4 py-3 text-right text-blue-800 font-semibold">Price</th>
                        <th className="px-4 py-3 text-right text-blue-800 font-semibold">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOrder.items?.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-gray-900 font-medium text-sm break-words max-w-xs">{item.menuItemId?.name || item.name || 'Item'}</span>
                              {item.bogoApplied && (
                                <span className="bg-green-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                                  BOGO
                                </span>
                              )}
                            </div>
                            {item.customizations && item.customizations.length > 0 && (
                              <div className="text-xs text-gray-600 mt-1 italic break-words max-w-xs">
                                + {Array.isArray(item.customizations) ? item.customizations.join(', ') : item.customizations}
                              </div>
                            )}
                            {item.bogoApplied && item.bogoDetails && (
                              <div className="text-xs text-green-600 font-bold mt-1">
                                🎁 {item.bogoDetails.offerText} ({item.bogoDetails.freeQuantity} free)
                              </div>
                            )}
                          </td>
                          <td className="py-3 text-center text-sm text-gray-700">{item.quantity}</td>
                          <td className="py-3 text-right text-sm text-gray-700">₹{item.price}</td>
                          <td className="py-3 text-right text-sm font-medium text-gray-900">₹{(item.quantity * item.price).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Pricing Details */}
              <div className="mb-6">
                <h3 className="mb-4 text-blue-600 text-lg font-semibold">💰 Pricing Details</h3>
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">₹{selectedOrder.pricing?.subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">GST Amount:</span>
                      <span className="font-medium">₹{selectedOrder.pricing?.gstAmount?.toFixed(2) || '0.00'}</span>
                    </div>
                    {selectedOrder.pricing?.deliveryCharge > 0 && (
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Delivery Charge:</span>
                        <span className="font-medium">₹{selectedOrder.pricing?.deliveryCharge?.toFixed(2) || '0.00'}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Discount:</span>
                      <span className="font-medium text-green-600">-₹{selectedOrder.pricing?.discount?.toFixed(2) || '0.00'}</span>
                    </div>
                    <div className="border-t pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="font-semibold text-gray-800">Total Amount:</span>
                        <span className="font-bold text-lg text-blue-600">₹{selectedOrder.pricing?.total?.toFixed(2) || '0.00'}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Information */}
              {selectedOrder.paymentDetails && (
                <div className="mb-6">
                  <h3 className="mb-4 text-blue-600 text-lg font-semibold">💳 Payment Information</h3>
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="text-sm">
                        <span className="text-gray-600 font-medium">Payment Method:</span>
                        <span className="ml-2 font-medium capitalize">{selectedOrder.paymentMethod || 'N/A'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600 font-medium">Payment Status:</span>
                        <span className="ml-2 font-medium">{selectedOrder.paymentStatus || 'Pending'}</span>
                      </div>
                      <div className="text-sm">
                        <span className="text-gray-600 font-medium">Cash Amount:</span>
                        <span className="ml-2 font-medium">
                          ₹{selectedOrder.paymentDetails?.cashAmount ?? 0}
                        </span>
                      </div>

                      <div className="text-sm">
                        <span className="text-gray-600 font-medium">Online Amount:</span>
                        <span className="ml-2 font-medium">
                          ₹{selectedOrder.paymentDetails?.onlineAmount ?? 0}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors flex items-center gap-2" onClick={() => {
                setShowDetailsModal(false);
                generateKOT(selectedOrder._id);
              }}>
                <FiPrinter /> Print KOT
              </button>
              {
                (
                  selectedOrder.orderType !== 'dine-in' ||
                  (selectedOrder.orderType === 'dine-in' && selectedOrder.tableId?.tableNumber === 0)
                ) && (
                  <button
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
                    onClick={() => {
                      setShowDetailsModal(false);
                      openBillModal(selectedOrder);
                    }}
                  >
                    <FiFileText /> {selectedOrder.billGenerated ? 'View Bill' : 'Generate Bill'}
                  </button>
                )
              }

            </div>
          </div>
        </div>
      )}

      {/* Bill Modal */}
      {showBillModal && selectedOrder && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
              <h2 className="text-xl font-bold text-gray-800">
                {selectedOrder.billGenerated ? 'View Bill' : 'Generate Bill'} - {selectedOrder.orderNumber}
              </h2>
              <button className="text-gray-500 hover:text-gray-700 text-2xl font-bold leading-none transition-colors" onClick={() => setShowBillModal(false)}>×</button>
            </div>
            <div className="flex-1 overflow-y-auto p-6">

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount Type</label>
                  <select
                    className={`w-full px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${selectedOrder.billGenerated || isMc ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    value={discount.type}
                    onChange={(e) => {
                      setDiscount({ ...discount, type: e.target.value });

                      let total = Number(selectedOrder.pricing.total || 0);
                      if (Number(discount.amount) > 0) {
                        if (e.target.value === 'percentage') {
                          total = total * (1 - Number(discount.amount) / 100);
                        } else {
                          total = total - Number(discount.amount);
                        }
                      }
                      setCashOnly(false);
                      setPayment((prev) => ({ ...prev, onlineAmount: Number(total.toFixed(2)), cashAmount: 0 }))
                    }}
                    disabled={selectedOrder.billGenerated || isMc}
                  >
                    <option value="percentage">Percentage (%)</option>
                    <option value="flat">Flat Amount (₹)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Discount {discount.type === 'percentage' ? 'Percentage' : 'Amount'}</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${selectedOrder.billGenerated || isMc ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    value={discount.amount}
                    inputMode="decimal"
                    placeholder={discount.type === 'percentage' ? 'Enter percentage' : 'Enter amount'}
                    onChange={(e) => {
                      if (selectedOrder.billGenerated || isMc) return; // Prevent changes if bill generated or MC is checked

                      let value = e.target.value.replace(/[^0-9.]/g, '');

                      // Allow only one decimal
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }

                      // Max 100 for percentage
                      if (discount.type === 'percentage' && Number(value) > 100) {
                        value = '100';
                      }

                      setDiscount({
                        ...discount,
                        amount: value
                      });
                    }}
                    onBlur={() => {
                      if (selectedOrder.billGenerated || isMc) return; // Prevent changes if bill generated or MC is checked

                      let total = Number(selectedOrder.pricing.total || 0);
                      if (Number(discount.amount) > 0) {
                        if (discount.type === 'percentage') {
                          total = total * (1 - Number(discount.amount) / 100);
                        } else {
                          total = total - Number(discount.amount);
                        }
                      }
                      setCashOnly(false);
                      setPayment((prev) => ({ ...prev, onlineAmount: Number(total.toFixed(2)), cashAmount: 0 }))
                    }}
                    disabled={selectedOrder.billGenerated || isMc}
                  />
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
                <div>
                  <label className="flex items-center justify-start gap-7 text-sm font-medium text-gray-700 mb-2">
                    <span>Cash Amount (₹)</span>
                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                          selectedOrder.billGenerated || isMc ? 'bg-gray-100 cursor-not-allowed' : ''
                        }`}
                        checked={cashOnly}
                        onChange={(e) => {
                          if (selectedOrder.billGenerated || isMc) return;
                          setCashOnly(e.target.checked);

                          if (e.target.checked) {
                            let cashAmount = Number(payment.cashAmount) + Number(payment.onlineAmount);
                            setPayment({ cashAmount: cashAmount, onlineAmount: 0 });
                          } else {
                            let onlineAmount = Number(payment.cashAmount) + Number(payment.onlineAmount);
                            setPayment({ cashAmount: 0, onlineAmount: onlineAmount });
                          }
                        }}
                        disabled={selectedOrder.billGenerated || isMc}
                      />

                      <span>Cash Only</span>
                    </div>
                  </label>
                  <div className="flex gap-3">
                    <input
                      type="text"
                      className={`flex-1 px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${selectedOrder.billGenerated || isMc ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                        }`}
                      value={payment.cashAmount}
                      inputMode="decimal"
                      placeholder="Enter cash amount"
                      onChange={(e) => {
                        if (selectedOrder.billGenerated || isMc || cashOnly) return; // Prevent changes if bill generated, MC is checked, or cashOnly is checked

                        let value = e.target.value.replace(/[^0-9.]/g, '');
                        const parts = value.split('.');
                        if (parts.length > 2) {
                          value = parts[0] + '.' + parts.slice(1).join('');
                        }
                        const billTotal = discount.type === 'percentage'
                          ? Number(selectedOrder.pricing?.total || 0) * (1 - Number(discount.amount || 0) / 100)
                          : Number(selectedOrder.pricing?.total || 0) - Number(discount.amount || 0);

                        // Auto-fill online amount with remaining balance
                        const cashAmt = Number(value) || 0;
                        const remainingAmt = billTotal - cashAmt;
                        setPayment({ cashAmount: value, onlineAmount: remainingAmt.toFixed(2) });
                      }}
                      disabled={selectedOrder.billGenerated || isMc || cashOnly}
                    />
                    
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Online Amount (₹)</label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${selectedOrder.billGenerated || isMc ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    value={payment.onlineAmount}
                    inputMode="decimal"
                    placeholder="Auto-filled or enter manually"
                    onChange={(e) => {
                      if (selectedOrder.billGenerated || isMc || cashOnly) return; // Prevent changes if bill generated, MC is checked, or cashOnly is checked

                      let value = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }
                      const billTotal = discount.type === 'percentage'
                        ? Number(selectedOrder.pricing?.total || 0) * (1 - Number(discount.amount || 0) / 100)
                        : Number(selectedOrder.pricing?.total || 0) - Number(discount.amount || 0);

                      // Auto-fill cash amount with remaining balance
                      const onlineAmt = Number(value) || 0;
                      const remainingAmt = billTotal - onlineAmt;
                      const cashAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                      setPayment({ cashAmount: cashAmt, onlineAmount: value });
                    }}
                    disabled={selectedOrder.billGenerated || isMc || cashOnly}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2">
  
                {/* LEFT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Cash Received from Customer (₹)
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      selectedOrder.billGenerated
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        : 'border-gray-300'
                    }`}
                    value={cashReceived}
                    inputMode="decimal"
                    placeholder="Enter cash received amount"
                    onChange={(e) => {
                      if (selectedOrder.billGenerated) return;
                      let value = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }
                      setCashReceived(value);
                    }}
                    disabled={selectedOrder.billGenerated}
                  />
                </div>

                {/* RIGHT */}
                <div>
                  {/* Spacer same as label height */}
                  <div className="mb-2 h-[20px]"></div>

                  {cashReceived &&
                    Number(cashReceived) > 0 &&
                    Number(payment.cashAmount) > 0 && (
                      <div className="p-2 bg-green-50 rounded border border-green-200">
                        <p className="text-sm font-medium text-green-800">
                          Refund/Change: ₹
                          {(Number(cashReceived) - Number(payment.cashAmount)).toFixed(2)}
                        </p>
                      </div>
                    )}
                </div>

              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-2 mb-2">

                {/* LEFT */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    GST Number
                  </label>
                  <input
                    type="text"
                    className={`w-full px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors ${
                      selectedOrder.billGenerated || isMc
                        ? 'bg-gray-100 text-gray-600 cursor-not-allowed'
                        : 'border-gray-300'
                    }`}
                    value={gstNumber}
                    placeholder="Enter GST Number"
                    onChange={(e) => {
                      if (selectedOrder.billGenerated || isMc) return;
                      setGstNumber(e.target.value.toUpperCase());
                    }}
                    disabled={selectedOrder.billGenerated || isMc}
                  />
                </div>

                {/* RIGHT */}
                <div className="flex items-end">
                  <label className="flex items-center space-x-3 cursor-pointer">
                    <input
                      type="checkbox"
                      className={`w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500 ${
                        selectedOrder.billGenerated ? 'bg-gray-100 cursor-not-allowed' : ''
                      }`}
                      checked={isMc}
                      onChange={(e) => {
                        if (selectedOrder.billGenerated) return;
                        setIsMc(e.target.checked);

                        if (e.target.checked) {
                          setDiscount({ amount: 0, type: 'percentage' });
                          setPayment({ cashAmount: 0, onlineAmount: 0 });
                          setComplimentary('');
                          setGstNumber('');
                        } else {
                          setDiscount({ amount: 0, type: 'percentage' });
                          const originalTotal = selectedOrder?.pricing?.total || 0;
                          setPayment({ cashAmount: '', onlineAmount: originalTotal });
                          setComplimentary('');
                        }
                      }}
                      disabled={selectedOrder.billGenerated}
                    />
                    <span className="text-sm font-medium text-gray-700">Is Complimentary</span>
                  </label>
                </div>

              </div>
              <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Complimentary</label>
                  <textarea
                    className={`w-full px-3 py-2 border rounded-lg focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 transition-colors resize-none ${selectedOrder.billGenerated ? 'bg-gray-100 text-gray-600 cursor-not-allowed' : 'border-gray-300'
                      }`}
                    value={complimentary}
                    placeholder="Enter complimentary(if any)"
                    rows="3"
                    onChange={(e) => {
                      if (selectedOrder.billGenerated) return;
                      setComplimentary(e.target.value);
                    }}
                    disabled={selectedOrder.billGenerated}
                  />
                </div>

              

              {(Number(payment.cashAmount) > 0 || Number(payment.onlineAmount) > 0) && (
                <div className="mt-2 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="font-semibold text-gray-800 mb-2">Payment Breakdown:</p>
                  {Number(payment.cashAmount) > 0 && (
                    <p className="text-green-600">💵 Cash: ₹{Number(payment.cashAmount).toFixed(2)}</p>
                  )}
                  {Number(payment.onlineAmount) > 0 && (
                    <p className="text-blue-600">💳 Online: ₹{Number(payment.onlineAmount).toFixed(2)}</p>
                  )}
                  <p className="font-bold mt-2 text-gray-900">
                    Total Paid: ₹{(Number(payment.cashAmount || 0) + Number(payment.onlineAmount || 0)).toFixed(2)}
                  </p>
                </div>
              )}

              <div className="mt-2 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <p className="text-sm text-gray-700"><strong>Subtotal:</strong> ₹{(selectedOrder.pricing?.subtotal || 0)}</p>
                {selectedOrder.pricing?.couponDiscount > 0 && (
                  <p style={{ color: '#16a34a' }}>
                    <strong>Coupon Discount{selectedOrder.couponDetails?.code ? ` (${selectedOrder.couponDetails.code})` : ''}:</strong> - ₹{Number(selectedOrder.pricing?.couponDiscount || 0).toFixed(2)}
                  </p>
                )}
                {Number(discount.amount) > 0 && (
                  <p>
                    <strong>Discount:</strong> - ₹{
                      discount.type === 'percentage'
                        ? ((Number(selectedOrder.pricing?.total || 0) * Number(discount.amount)) / 100).toFixed(2)
                        : Number(discount.amount).toFixed(2)
                    }
                  </p>
                )}
                {Number(selectedOrder.pricing?.vendorDiscount?.tableDiscountShare) > 0 && (
                  <p>
                    <strong>Discount:</strong> - ₹{
                      Number(selectedOrder.pricing?.vendorDiscount?.tableDiscountShare || 0).toFixed(2)
                    }
                  </p>
                )}
                <p className="text-sm text-gray-700"><strong>CGST:</strong> ₹{(Number(selectedOrder.pricing?.gstAmount || 0) / 2).toFixed(2)}</p>
                <p className="text-sm text-gray-700"><strong>SGST:</strong> ₹{(Number(selectedOrder.pricing?.gstAmount || 0) / 2).toFixed(2)}</p>
                {selectedOrder.pricing?.deliveryCharge > 0 && (
                  <p className="text-sm text-gray-700"><strong>Delivery Charge:</strong> ₹{Number(selectedOrder.pricing?.deliveryCharge || 0).toFixed(2)}</p>
                )}
                <p className="text-lg font-bold mt-2 pt-2 border-t-2 border-blue-600 text-gray-900">
                  <strong>Total Bill:</strong> ₹{
                    isMc ? '0.00' : (
                      discount.type === 'percentage'
                        ? (
                          Number(selectedOrder.pricing?.total || 0) * (1 - Number(discount.amount || 0) / 100)
                        ).toFixed(2)
                        : (
                          Number(selectedOrder.pricing?.total || 0) -
                          Number(discount.amount || 0)
                        ).toFixed(2)
                    )
                  }
                </p>

              </div>
            </div>
            <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
              <button className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-lg font-medium transition-colors" onClick={() => setShowBillModal(false)}>
                Cancel
              </button>
              <button className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors flex items-center gap-2" onClick={() => generateBill(selectedOrder.billGenerated)}>
                <FiFileText /> {selectedOrder.billGenerated ? 'Print Bill' : 'Generate & Print Bill'}
              </button>
            </div>
          </div>
        </div>
      )}

      {
        showCreateOrderModal &&
        <AddOrderModal 
          showCreateOrderModal={showCreateOrderModal}
          setShowCreateOrderModal={setShowCreateOrderModal}
        />
      }

      {/* Bill Update Request Modal */}
      {showBillUpdateModal && selectedOrderForUpdate && (
        <BillUpdateRequestModal
          order={selectedOrderForUpdate}
          onClose={() => {
            setShowBillUpdateModal(false);
            setSelectedOrderForUpdate(null);
          }}
          onRequestSubmitted={handleBillUpdateRequestSubmitted}
        />
      )}

      {/* Billing Modal */}
      {showBillingModal && selectedOrderForBilling && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: '500px' }}>
            <div className="modal-header">
              <h2>Payment Details - Order #{selectedOrderForBilling.orderNumber}</h2>
              <button className="close-btn" onClick={() => setShowBillingModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div style={{ marginBottom: '1.5rem', padding: '1rem', backgroundColor: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: '600' }}>Order Total:</span>
                  <span style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#2563eb' }}>
                    ₹{Number(selectedOrderForBilling.pricing?.total || 0).toFixed(2)}
                  </span>
                </div>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  Customer: {selectedOrderForBilling.customerId?.fullName || 'Guest'}
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div className="form-group">
                  <label>Cash Amount (₹)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={payment.cashAmount}
                    inputMode="decimal"
                    placeholder="Enter cash amount"
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }

                      const orderTotal = Number(selectedOrderForBilling.pricing?.total || 0);
                      const cashAmt = Number(value) || 0;
                      const remainingAmt = orderTotal - cashAmt;
                      const onlineAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                      setPayment({ cashAmount: value, onlineAmount: onlineAmt });
                    }}
                  />
                </div>
                <div className="form-group">
                  <label>Online Amount (₹)</label>
                  <input
                    type="text"
                    className="form-control"
                    value={payment.onlineAmount}
                    inputMode="decimal"
                    placeholder="Auto-filled or enter manually"
                    onChange={(e) => {
                      let value = e.target.value.replace(/[^0-9.]/g, '');
                      const parts = value.split('.');
                      if (parts.length > 2) {
                        value = parts[0] + '.' + parts.slice(1).join('');
                      }

                      const orderTotal = Number(selectedOrderForBilling.pricing?.total || 0);
                      const onlineAmt = Number(value) || 0;
                      const remainingAmt = orderTotal - onlineAmt;
                      const cashAmt = remainingAmt > 0 ? remainingAmt.toFixed(2) : '';

                      setPayment({ cashAmount: cashAmt, onlineAmount: value });
                    }}
                  />
                </div>
              </div>

              {(Number(payment.cashAmount) > 0 || Number(payment.onlineAmount) > 0) && (
                <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#f0fdf4', borderRadius: '8px', border: '1px solid #86efac' }}>
                  <p style={{ fontWeight: '600', marginBottom: '0.5rem', color: '#166534' }}>Payment Breakdown:</p>
                  {Number(payment.cashAmount) > 0 && (
                    <p style={{ color: '#16a34a', marginBottom: '0.25rem' }}>💵 Cash: ₹{Number(payment.cashAmount).toFixed(2)}</p>
                  )}
                  {Number(payment.onlineAmount) > 0 && (
                    <p style={{ color: '#2563eb', marginBottom: '0.25rem' }}>💳 Online: ₹{Number(payment.onlineAmount).toFixed(2)}</p>
                  )}
                  <p style={{ fontWeight: 'bold', marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid #86efac' }}>
                    Total Paid: ₹{(Number(payment.cashAmount || 0) + Number(payment.onlineAmount || 0)).toFixed(2)}
                  </p>
                  {(Number(payment.cashAmount || 0) + Number(payment.onlineAmount || 0)) !== Number(selectedOrderForBilling.pricing?.total || 0) && (
                    <p style={{ color: '#dc2626', fontSize: '0.85rem', marginTop: '0.5rem' }}>
                      ⚠️ Total must equal order amount
                    </p>
                  )}
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBillingModal(false)}>
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdatePayment}
                disabled={(Number(payment.cashAmount || 0) + Number(payment.onlineAmount || 0)) !== Number(selectedOrderForBilling.pricing?.total || 0)}
              >
                💰 Update Payment
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrdersManagement;
